import { Button, Dialog, Stack, Typography } from '@optimus-web/core';
import { lt, useDialog, useFeatureFlag } from '@mews/framework';
import { DataTestId, FeatureFlag } from '@mews-commander/enums';
import { OnboardingAuditStatus, useRepositoryMutation, useRepositorySuspenseQuery } from '@mews-commander/data-access';
import { useEnterpriseNavigate } from '@mews-commander/ui';
import { useCurrentEnterprise } from '@mews-commander/legacy-entities/src/enterprise';

interface RequestGoLiveButtonProps {
    status: OnboardingAuditStatus;
}

export const RequestGoLiveButton = ({ status }: RequestGoLiveButtonProps) => {
    const navigate = useEnterpriseNavigate();
    const { isOnboardingFinished } = useCurrentEnterprise();
    const [dialogProps, openDialog] = useDialog();
    const onboardingRequestGoLiveButtonEnabled = useFeatureFlag(FeatureFlag.onboardingRequestGoLiveButtonEnabled);

    const { data } = useRepositorySuspenseQuery('enterprises', 'getOnboardingDetails', {});

    const { mutateAsync, isPending } = useRepositoryMutation('enterprises', 'requestGoLiveValidation', {
        invalidateQueries: {
            enterprises: ['getOnboardingDetails'],
        },
    });

    if (!onboardingRequestGoLiveButtonEnabled || isOnboardingFinished || !data.isDigitalOnboarding) return null;

    const isAuditStatusFailed = status === OnboardingAuditStatus.NoGo || status === OnboardingAuditStatus.Incomplete;

    const handleClick = async () => {
        await mutateAsync({});
        openDialog();
    };

    const handleCloseDialog = () => {
        dialogProps.onClose();
        void navigate({ to: 'Settings/GoLiveAudits/ActivityLog' });
    };

    const disableButton = isAuditStatusFailed || data.isValidationRequested || !data.goLiveDate;

    let helperText: React.ReactNode | null = null;
    if (isAuditStatusFailed) {
        helperText = lt('GoLiveAuditsButtonRequestHelperOnceAllChecksPass');
    } else if (data.isValidationRequested) {
        helperText = lt('GoLiveAuditsButtonRequestHelperValidationRequested');
    } else if (!data.goLiveDate) {
        helperText = lt('GoLiveAuditsButtonRequestHelperNoGoLiveDate');
    }

    return (
        <>
            <Stack vertical spacing="50">
                <Button
                    variant="primary"
                    size="medium"
                    disabled={disableButton}
                    loading={isPending}
                    onClick={handleClick}
                    type="submit"
                    data-test-id={DataTestId.RequestGoLiveValidationButton}
                >
                    {lt('GoLiveAuditsButtonRequestGoLiveValidation')}
                </Button>
                {helperText && (
                    <Typography textStyle="bodySmall" variant="tertiary" align="center" as="div">
                        {helperText}
                    </Typography>
                )}
            </Stack>
            <Dialog
                title={lt('GoLiveAuditsRequestGoLiveDialogTitle')}
                footer={{
                    primary: (
                        <Button onClick={handleCloseDialog} variant="primary" size="medium" type="submit">
                            {lt('Ok')}
                        </Button>
                    ),
                }}
                {...dialogProps}
            >
                {lt('GoLiveAuditsRequestGoLiveDialogText')}
            </Dialog>
        </>
    );
};
